/**
 * Sentry initialization — opt-in via env var.
 *
 * Set VITE_SENTRY_DSN in .env.local or in your Vercel environment vars.
 * Without a DSN, this is a no-op (zero overhead, zero events sent).
 *
 * Get a DSN from: https://sentry.io → Settings → Projects → Client Keys
 * Free tier covers ~5k events/month — plenty for this app's size.
 *
 * Debugging from the browser console (Sentry v8+ API):
 *   window.Sentry.captureMessage('test')
 *   window.Sentry.captureException(new Error('test'))
 *   window.__sentryTest()                 // built-in smoke test
 */
import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || initialized) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Sample 10% of normal transactions for performance, 100% of errors
    tracesSampleRate: 0.1,
    // We always want errors to flow when a DSN is present — including from
    // preview deploys. The old PROD-only gate was masking events that
    // SHOULD have been visible.
    enabled: true,
    // Helps us confirm in the console that init() actually ran
    debug: false,
    // Drop common noise: cancelled fetches, extension errors, etc.
    // Most of these come from Chrome extensions installed in the user's
    // browser — none of them are bugs in our code.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network request failed',
      // Classic Chrome extension noise: Grammarly, 1Password, Loom,
      // translators, etc. fire this when their content script's message
      // channel closes mid-request. Has nothing to do with the page.
      'A listener indicated an asynchronous response by returning true',
      'message channel closed before a response',
      // Service worker / messaging noise during page navigation
      'The operation was aborted',
      // Old/uninstalled extensions can throw this on unrelated pages
      'Extension context invalidated',
      // chrome-extension:// scripts that fail to load
      /chrome-extension:\/\//i,
      // moz-extension noise on Firefox
      /moz-extension:\/\//i,
    ],
    // Drop frames originating from extension contexts entirely so a
    // single noisy extension can't fill the issues feed.
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
    beforeSend(event) {
      // Strip phone numbers and tokens from breadcrumbs / extra data
      // before the event leaves the browser (defense in depth).
      const sanitize = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        for (const key of Object.keys(obj)) {
          if (/phone|fcm|token|email/i.test(key)) obj[key] = '[redacted]';
          else if (typeof obj[key] === 'object') sanitize(obj[key]);
        }
        return obj;
      };
      sanitize(event.extra);
      sanitize(event.contexts);
      sanitize(event.breadcrumbs);
      return event;
    },
  });

  initialized = true;

  // Expose for easy debugging from DevTools.
  // This is safe — Sentry's public API is meant to be used at runtime.
  if (typeof window !== 'undefined') {
    window.Sentry = Sentry;
    // One-shot smoke test you can run from the console:
    //   window.__sentryTest()
    // Returns a Promise that resolves after the event is flushed to the
    // Sentry servers. If the network call succeeds, the test event should
    // appear in your dashboard within ~30s.
    window.__sentryTest = async () => {
      const id = Sentry.captureMessage(
        `Smoke test desde DevTools — ${new Date().toISOString()}`,
        'info'
      );
      console.log('[Sentry] event id:', id);
      const ok = await Sentry.flush(5000);
      console.log('[Sentry] flush result:', ok ? '✅ enviado' : '❌ no llegó (probable ad-blocker o sin red)');
      return ok;
    };
    console.info('[Sentry] inicializado. Prueba en consola:\n  window.__sentryTest()');
  }
}

export { Sentry };
